import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { getOffersByGardener, type OfferResponse } from '@services/offers';
import { mapBackendOfferToFrontend } from '@pages/seeker/OffersForMyPlan';
import type { Offer } from '@data/offers';
import { useAuth } from '@services/auth';
import { useAdminViewAs, getAdminEffectiveCompanyOrGardenerIdNum, getMessagingActingUserId } from '../../lib/adminViewAs';
import { getOffers } from '@data/offers';
import FilterToggle from '@components/filters/FilterToggle';
import FiltersPanel from '@components/filters/FiltersPanel';
import ConversationThread from '@components/ConversationThread';
import {
	readPlanThumbSession,
	writePlanThumbSession
} from '@services/projectsSessionCache';
import { getPlanById } from '@services/plans';
import { getImageUrl } from '@utils/imageUrl';
import { useI18n } from '../../i18n';

type SentOffersProps = {
	/** When true, used inside Projects tab; hides main heading and tightens layout */
	embedded?: boolean;
};

type OfferListScope = 'active' | 'past' | 'all';

const PLAN_THUMB_FALLBACK =
	'https://images.unsplash.com/photo-1416879595882-3373a0480b5b?w=800&h=400&fit=crop';

function offerMatchesListScope(offer: Offer, scope: OfferListScope): boolean {
	if (scope === 'all') return true;
	if (scope === 'active') return offer.status !== 'rejected';
	return offer.status === 'rejected';
}

function parseOfferPlanNumericId(planId: string): number {
	const raw = planId.replace(/^db-/, '').trim();
	const n = parseInt(raw, 10);
	return Number.isNaN(n) ? NaN : n;
}

export default function SentOffers({ embedded = false }: SentOffersProps) {
	const { user } = useAuth();
	const { viewAsTarget } = useAdminViewAs();
	const { language } = useI18n();
	const copy = (en: string, de: string, hr: string) =>
		language === 'de' ? de : language === 'hr' ? hr : en;
	const locale = language === 'de' ? 'de-DE' : language === 'hr' ? 'hr-HR' : 'en-GB';
	const navigate = useNavigate();
	const [offers, setOffers] = useState<Offer[]>([]);
	const [isLoading, setIsLoading] = useState(true);
	const [searchTerm, setSearchTerm] = useState('');
	const [selectedStatuses, setSelectedStatuses] = useState<Array<Offer['status']>>([]);
	const [isFiltersOpen, setIsFiltersOpen] = useState(false);
	const [messageOfferId, setMessageOfferId] = useState<string | null>(null);
	const [listScope, setListScope] = useState<OfferListScope>('active');
	const [planImageByPlanId, setPlanImageByPlanId] = useState<Record<number, string>>({});

	useEffect(() => {
		if (!user) {
			setIsLoading(false);
			setOffers([]);
			return;
		}
		const lens = getAdminEffectiveCompanyOrGardenerIdNum(user, viewAsTarget);
		let userIdNum: number;
		if (user.role === 'admin') {
			if (lens == null) {
				setIsLoading(false);
				setOffers([]);
				return;
			}
			userIdNum = lens;
		} else if (user.role === 'gardener' || user.role === 'company') {
			userIdNum = typeof user.id === 'string' ? parseInt(user.id, 10) : user.id;
		} else {
			setIsLoading(false);
			setOffers([]);
			return;
		}
		if (isNaN(userIdNum)) {
			setIsLoading(false);
			setOffers([]);
			return;
		}

		let isMounted = true;
		const load = async () => {
			setIsLoading(true);
			try {
				const apiOffers = await getOffersByGardener(userIdNum);
				const mapped = apiOffers.map((o: OfferResponse) => mapBackendOfferToFrontend(o));
				if (isMounted) setOffers(mapped);
			} catch (err) {
				console.error('Failed to load sent offers:', err);
				// Fallback: filter localStorage offers by current user
				const local = getOffers().filter((o) => o.gardenerId === String(user.id));
				if (isMounted) setOffers(local);
			} finally {
				if (isMounted) setIsLoading(false);
			}
		};
		load();
		return () => { isMounted = false; };
	}, [user?.id, user?.role, viewAsTarget]);

	const handleOffersUpdate = () => {
		if (!user) return;
		const lens = getAdminEffectiveCompanyOrGardenerIdNum(user, viewAsTarget);
		let userIdNum: number;
		if (user.role === 'admin') {
			if (lens == null) return;
			userIdNum = lens;
		} else {
			userIdNum = typeof user.id === 'string' ? parseInt(user.id, 10) : user.id;
		}
		if (isNaN(userIdNum)) return;
		getOffersByGardener(userIdNum)
			.then((apiOffers) => setOffers(apiOffers.map((o: OfferResponse) => mapBackendOfferToFrontend(o))))
			.catch(() => setOffers(getOffers().filter((o) => o.gardenerId === String(userIdNum))));
	};

	useEffect(() => {
		window.addEventListener('offersUpdated', handleOffersUpdate);
		return () => window.removeEventListener('offersUpdated', handleOffersUpdate);
	}, [user?.id, viewAsTarget]);

	const statusLabels: Record<Offer['status'], string> = {
		pending: copy('Pending', 'Ausstehend', 'Na čekanju'),
		accepted: copy('Accepted', 'Akzeptiert', 'Prihvaćeno'),
		rejected: copy('Rejected', 'Abgelehnt', 'Odbijeno')
	};

	const scopeCounts = useMemo(() => {
		let active = 0;
		let past = 0;
		for (const o of offers) {
			if (o.status !== 'rejected') active += 1;
			else past += 1;
		}
		return { active, past, total: offers.length };
	}, [offers]);

	const filteredOffers = useMemo(() => {
		const term = searchTerm.trim().toLowerCase();
		return offers.filter((o) => {
			if (!offerMatchesListScope(o, listScope)) return false;
			const matchesSearch =
				!term ||
				(o.planTitle && o.planTitle.toLowerCase().includes(term)) ||
				(o.planCity && o.planCity.toLowerCase().includes(term)) ||
				(o.planId && o.planId.toLowerCase().includes(term));
			const matchesStatus =
				selectedStatuses.length === 0 || selectedStatuses.includes(o.status);
			return matchesSearch && matchesStatus;
		});
	}, [offers, searchTerm, selectedStatuses, listScope]);

	const displayOffers = useMemo(() => {
		const list = [...filteredOffers];
		const rank = (s: Offer['status']) => (s === 'pending' ? 0 : s === 'accepted' ? 1 : 2);
		list.sort((a, b) => {
			const dr = rank(a.status) - rank(b.status);
			if (dr !== 0) return dr;
			const ta = new Date(a.createdAt).getTime();
			const tb = new Date(b.createdAt).getTime();
			return tb - ta;
		});
		return list;
	}, [filteredOffers]);

	useEffect(() => {
		if (!offers.length) return;
		let cancelled = false;
		const uniquePlanIds = [
			...new Set(
				offers
					.map((o) => parseOfferPlanNumericId(o.planId))
					.filter((n) => !Number.isNaN(n))
			)
		];
		if (!uniquePlanIds.length) return;

		const fromCache: Record<number, string> = {};
		const missing: number[] = [];
		for (const id of uniquePlanIds) {
			const hit = readPlanThumbSession(id);
			if (hit) fromCache[id] = hit;
			else missing.push(id);
		}
		if (Object.keys(fromCache).length > 0) {
			setPlanImageByPlanId((prev) => ({ ...fromCache, ...prev }));
		}
		if (!missing.length) return;

		(async () => {
			const entries = await Promise.all(
				missing.map(async (planId) => {
					try {
						const pl = await getPlanById(planId);
						const raw =
							pl.after_image_url && !pl.after_image_url.includes('unsplash.com')
								? pl.after_image_url
								: pl.before_image_url || PLAN_THUMB_FALLBACK;
						const url = getImageUrl(raw) || raw;
						return [planId, url] as const;
					} catch {
						return [planId, PLAN_THUMB_FALLBACK] as const;
					}
				})
			);
			if (cancelled) return;
			for (const [id, url] of entries) writePlanThumbSession(id, url);
			setPlanImageByPlanId((prev) => {
				const next = { ...prev };
				for (const [id, url] of entries) next[id] = url;
				return next;
			});
		})();

		return () => {
			cancelled = true;
		};
	}, [offers]);

	const toggleStatus = (status: Offer['status']) => {
		setSelectedStatuses((prev) =>
			prev.includes(status) ? prev.filter((s) => s !== status) : [...prev, status]
		);
	};

	const openMessageModal = (offerId: string) => {
		window.dispatchEvent(new Event('app:clear-search-focus'));
		if (document.activeElement instanceof HTMLElement) {
			document.activeElement.blur();
		}
		document.querySelectorAll<HTMLInputElement>('input[type="search"]').forEach((input) => input.blur());
		setTimeout(() => {
			if (document.activeElement instanceof HTMLElement) {
				document.activeElement.blur();
			}
			document.querySelectorAll<HTMLInputElement>('input[type="search"]').forEach((input) => input.blur());
		}, 0);
		setMessageOfferId(offerId);
	};

	const activeFiltersCount = selectedStatuses.length;

	if (user && user.role !== 'gardener' && user.role !== 'company') {
		return (
			<div className="alert alert-info">
				{copy('Sent Offers is only available for gardeners and companies.', 'Gesendete Angebote sind nur für Gärtner und Unternehmen verfügbar.', 'Poslane ponude dostupne su samo vrtlarima i tvrtkama.')}
			</div>
		);
	}

	return (
		<>
			{embedded && (
				<p className="text-muted small mb-3">
					{copy('Offers you sent from the Marketplace. Accepted offers become projects.', 'Angebote, die Sie vom Marktplatz gesendet haben. Akzeptierte Angebote werden zu Projekten.', 'Ponude koje ste poslali s Marketplacea. Prihvaćene ponude postaju projekti.')}
				</p>
			)}
			{!embedded && (
				<div className="mb-3">
					<h1 className="h3 mb-0">{copy('Sent Offers', 'Gesendete Angebote', 'Poslane ponude')}</h1>
					<p className="text-muted small mb-0 mt-1">
						{copy('Overview of all offers you sent. Open a plan to edit or resend.', 'Übersicht aller von Ihnen gesendeten Angebote. Öffnen Sie einen Plan, um ihn zu bearbeiten oder erneut zu senden.', 'Pregled svih ponuda koje ste poslali. Otvorite plan za uređivanje ili ponovno slanje.')}
					</p>
				</div>
			)}

			<div className="d-flex flex-wrap align-items-center justify-content-between gap-3 mb-2">
				<div
					className="btn-group flex-wrap"
					role="group"
					aria-label={copy('Offer list scope', 'Angebotsliste', 'Opseg ponuda')}
				>
					<button
						type="button"
						className={`btn btn-sm ${listScope === 'active' ? 'btn-success' : 'btn-outline-secondary'}`}
						onClick={() => setListScope('active')}
					>
						<i className="bi bi-hourglass-split me-1" aria-hidden />
						{copy('Open', 'Offen', 'Otvoreno')}
						<span className="badge bg-light text-dark ms-1">{scopeCounts.active}</span>
					</button>
					<button
						type="button"
						className={`btn btn-sm ${listScope === 'past' ? 'btn-success' : 'btn-outline-secondary'}`}
						onClick={() => setListScope('past')}
					>
						<i className="bi bi-archive me-1" aria-hidden />
						{copy('Declined', 'Abgelehnt', 'Odbijeno')}
						<span className="badge bg-light text-dark ms-1">{scopeCounts.past}</span>
					</button>
					<button
						type="button"
						className={`btn btn-sm ${listScope === 'all' ? 'btn-success' : 'btn-outline-secondary'}`}
						onClick={() => setListScope('all')}
					>
						<i className="bi bi-list-ul me-1" aria-hidden />
						{copy('All', 'Alle', 'Sve')}
						<span className="badge bg-light text-dark ms-1">{scopeCounts.total}</span>
					</button>
				</div>
				<div className="d-flex flex-wrap gap-2 justify-content-end flex-grow-1">
					<input
						type="search"
						className="form-control"
						placeholder={copy('Search by plan title or location...', 'Suche nach Plantitel oder Standort...', 'Pretraži po naslovu plana ili lokaciji...')}
						value={searchTerm}
						onChange={(e) => setSearchTerm(e.target.value)}
						style={{ minWidth: 200, maxWidth: 320 }}
					/>
					<FilterToggle
						isOpen={isFiltersOpen}
						onToggle={() => setIsFiltersOpen((prev) => !prev)}
						activeCount={activeFiltersCount}
					/>
					<Link to="/app/plans" className="btn btn-success">
						<i className="bi bi-shop me-1"></i>{copy('Marketplace', 'Marktplatz', 'Tržnica')}
					</Link>
				</div>
			</div>
			<p className="text-muted small mb-3">
				{listScope === 'active'
					? copy(
							'Pending and accepted offers. Declined offers are under Declined.',
							'Ausstehende und akzeptierte Angebote. Abgelehnte finden Sie unter „Abgelehnt“.',
							'Ponude na čekanju i prihvaćene. Odbijene su pod „Odbijeno“.'
						)
					: listScope === 'past'
						? copy(
								'Offers the seeker declined. Open shows what is still live.',
								'Vom Gartensuchenden abgelehnte Angebote. Unter „Offen“ sehen Sie laufende Angebote.',
								'Ponude koje je vlasnik odbio. „Otvoreno“ prikazuje aktivne ponude.'
							)
						: copy('Every offer you have sent.', 'Alle Angebote, die Sie gesendet haben.', 'Sve ponude koje ste poslali.')}
			</p>

			<FiltersPanel
				isOpen={isFiltersOpen}
				onApply={() => setIsFiltersOpen(false)}
				onReset={() => setSelectedStatuses([])}
			>
				<div className="mb-2 text-muted text-uppercase small">{copy('Status', 'Status', 'Status')}</div>
				{(['pending', 'accepted', 'rejected'] as const).map((status) => (
					<div className="form-check" key={status}>
						<input
							className="form-check-input"
							type="checkbox"
							id={`sent-offers-status-${status}`}
							checked={selectedStatuses.includes(status)}
							onChange={() => toggleStatus(status)}
						/>
						<label className="form-check-label" htmlFor={`sent-offers-status-${status}`}>
							{statusLabels[status]}
						</label>
					</div>
				))}
			</FiltersPanel>

			{isLoading ? (
				<div className="text-center py-5">
					<div className="spinner-border text-success" role="status">
						<span className="visually-hidden">{copy('Loading...', 'Laden...', 'Učitavanje...')}</span>
					</div>
					<p className="text-muted mt-2 mb-0">{copy('Loading your offers...', 'Ihre Angebote werden geladen...', 'Učitavanje vaših ponuda...')}</p>
				</div>
			) : displayOffers.length === 0 ? (
				<div className="card border-0 shadow-sm">
					<div className="card-body text-center py-5">
						<i className="bi bi-send-x text-muted" style={{ fontSize: '3rem' }}></i>
						<h5 className="mt-3 mb-2">{copy('No offers yet', 'Noch keine Angebote', 'Još nema ponuda')}</h5>
						<p className="text-muted mb-4">
							{offers.length === 0
								? copy('Offers you send from the Marketplace will appear here.', 'Angebote, die Sie vom Marktplatz senden, erscheinen hier.', 'Ponude koje pošaljete s Marketplacea pojavit će se ovdje.')
								: listScope === 'active' && scopeCounts.active === 0 && scopeCounts.past > 0
									? copy(
											'You only have declined offers here. Switch to Declined or All to see them.',
											'Sie haben nur abgelehnte Angebote. Wechseln Sie zu „Abgelehnt“ oder „Alle“.',
											'Ovdje su samo odbijene ponude. Odaberite „Odbijeno“ ili „Sve“ da ih vidite.'
										)
									: copy('No offers match your filters.', 'Keine Angebote entsprechen Ihren Filtern.', 'Nijedna ponuda ne odgovara vašim filterima.')}
						</p>
						{listScope === 'active' && scopeCounts.active === 0 && scopeCounts.past > 0 && (
							<button
								type="button"
								className="btn btn-outline-secondary btn-sm me-2 mb-2"
								onClick={() => setListScope('past')}
							>
								{copy('Show declined', 'Abgelehnte anzeigen', 'Prikaži odbijene')}
							</button>
						)}
						<Link to="/app/plans" className="btn btn-success">
							<i className="bi bi-shop me-1"></i>{copy('Browse plans & send an offer', 'Pläne durchsuchen & Angebot senden', 'Pregledaj planove i pošalji ponudu')}
						</Link>
					</div>
				</div>
			) : (
				<div className="row g-4">
					{displayOffers.map((offer) => {
						const planNumericId = offer.planId.replace(/^db-/, '');
						const planNum = parseOfferPlanNumericId(offer.planId);
						const planDisplayName = offer.planTitle?.trim() || `Plan #${planNumericId}`;
						const thumb =
							!Number.isNaN(planNum) && planImageByPlanId[planNum]
								? planImageByPlanId[planNum]
								: PLAN_THUMB_FALLBACK;
						const quoteLabel =
							offer.bands.mid > 0
								? `${offer.bands.mid.toLocaleString(locale)} €`
								: copy('Quote on request', 'Preis auf Anfrage', 'Cijena na upit');
						return (
							<div key={offer.id} className="col-12 col-lg-6">
								<div
									role="link"
									tabIndex={0}
									className="card border-0 shadow-sm overflow-hidden plan-card h-100"
									style={{ cursor: 'pointer' }}
									onClick={() => navigate(`/app/sent-offer/${offer.id}`)}
									onKeyDown={(e) => {
										if (e.key === 'Enter' || e.key === ' ') {
											e.preventDefault();
											navigate(`/app/sent-offer/${offer.id}`);
										}
									}}
								>
									<div className="row g-0">
										<div className="col-12 col-sm-4 col-lg-5">
											<div className="ratio ratio-4x3 h-100" style={{ minHeight: '140px' }}>
												<img
													src={thumb}
													alt=""
													className="object-fit-cover w-100 h-100"
													style={{ objectFit: 'cover' }}
													loading="lazy"
												/>
											</div>
										</div>
										<div className="col-12 col-sm-8 col-lg-7">
											<div className="card-body p-3 p-md-4 d-flex flex-column h-100">
												<div className="d-flex flex-wrap align-items-center gap-2 mb-2">
													<span
														className={`badge ${
															offer.status === 'accepted'
																? 'text-bg-success'
																: offer.status === 'rejected'
																	? 'text-bg-secondary'
																	: 'text-bg-warning text-dark'
														}`}
													>
														{statusLabels[offer.status]}
													</span>
													<span className="badge text-bg-light text-muted border">
														{copy('Typical quote', 'Typische Preismitte', 'Tipična ponuda')}
													</span>
												</div>
												<h2 className="h5 mb-1 fw-bold">{planDisplayName}</h2>
												<p className="text-muted small mb-2">
													{copy('Plan', 'Plan', 'Plan')} #{planNumericId}
													{offer.planCity ? (
														<>
															{' · '}
															<i className="bi bi-geo-alt me-1" aria-hidden />
															{offer.planCity}
														</>
													) : null}
												</p>
												<div className="mb-3">
													<div className="fw-semibold fs-6">{quoteLabel}</div>
													<div className="text-muted small">
														<i className="bi bi-calendar3 me-1" aria-hidden />
														{copy('Sent', 'Gesendet', 'Poslano')}{' '}
														{offer.createdAt
															? new Date(offer.createdAt).toLocaleDateString(locale, {
																	day: 'numeric',
																	month: 'short',
																	year: 'numeric'
																})
															: '—'}
													</div>
												</div>
												<div className="mt-auto d-flex flex-wrap gap-2 pt-1">
													<button
														type="button"
														className="btn btn-outline-primary btn-sm"
														onClick={(e) => {
															e.stopPropagation();
															openMessageModal(offer.id);
														}}
													>
														<i className="bi bi-chat-dots me-1" aria-hidden />
														{copy('Message', 'Nachricht', 'Poruka')}
													</button>
													<button
														type="button"
														className="btn btn-success btn-sm"
														onClick={(e) => {
															e.stopPropagation();
															navigate(`/app/sent-offer/${offer.id}`);
														}}
													>
														<i className="bi bi-eye me-1" aria-hidden />
														{copy('View offer', 'Angebot ansehen', 'Pogledaj ponudu')}
													</button>
												</div>
											</div>
										</div>
									</div>
								</div>
							</div>
						);
					})}
				</div>
			)}
			{messageOfferId && user && (() => {
				const offerIdNum = parseInt(messageOfferId, 10);
				const userIdNum = getMessagingActingUserId(user, viewAsTarget);
				if (isNaN(offerIdNum) || userIdNum == null || isNaN(userIdNum)) return null;
				return (
					<>
						<div className="modal fade show" style={{ display: 'block', zIndex: 20000 }} role="dialog" aria-modal="true" aria-labelledby="sentOfferMessageModalTitle">
							<div className="modal-dialog modal-dialog-centered">
								<div className="modal-content">
									<div className="modal-header">
										<h5 className="modal-title" id="sentOfferMessageModalTitle">{copy('Message about this offer', 'Nachricht zu diesem Angebot', 'Poruka o ovoj ponudi')}</h5>
										<button type="button" className="btn-close" aria-label="Close" onClick={() => setMessageOfferId(null)} />
									</div>
									<div className="modal-body">
										<ConversationThread
											variant="offer"
											offerId={offerIdNum}
											currentUserId={userIdNum}
											title={copy('Conversation with seeker', 'Unterhaltung mit Suchendem', 'Razgovor s vlasnikom')}
											onClose={() => setMessageOfferId(null)}
										/>
									</div>
								</div>
							</div>
						</div>
						<div className="modal-backdrop fade show" style={{ zIndex: 19990 }} />
					</>
				);
			})()}
		</>
	);
}
